import { z } from 'zod';

import {
  NOTIFICATION_CATEGORIES,
  NotificationCategorySchema,
} from '../notification-category/notification-category';

/**
 * One category's switch per preference channel.
 *
 * Written as a literal rather than built from `PREFERENCE_CHANNELS` so the
 * inferred type is exact; `notification-preference.spec.ts` asserts the keys
 * still equal that list, so a channel added there fails a test instead of
 * silently missing a switch.
 */
export const ChannelPreferencesSchema = z
  .object({ push: z.boolean(), inApp: z.boolean(), email: z.boolean() })
  .strict();

export type ChannelPreferencesValue = z.infer<typeof ChannelPreferencesSchema>;

/**
 * A member's notification preferences: every declared category, resolved.
 *
 * **Complete rather than sparse, and that is the contract.** A member whose
 * stored rows cover four categories still gets eleven here, filled in from
 * `notifications.defaultPreferences` and then from the coded default — so the
 * mobile screen renders every switch on the first read, with no "unset" third
 * state to design around, and a category added by a later phase appears for
 * members who registered before it existed.
 */
export const NotificationPreferencesSchema = z
  .object(
    Object.fromEntries(
      NOTIFICATION_CATEGORIES.map((category) => [category, ChannelPreferencesSchema]),
    ) as Record<(typeof NOTIFICATION_CATEGORIES)[number], typeof ChannelPreferencesSchema>,
  )
  .strict();

export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

/**
 * One category as the mobile preferences screen draws it.
 *
 * `alwaysOn` is what makes the screen honest: an enforcement notice, a
 * verification decision and a legal re-consent bypass preferences
 * (`03-notifications.md`), so offering a switch that silently does nothing would
 * be a lie the member only discovers by not being able to turn something off.
 */
export const NotificationPreferenceRowSchema = z
  .object({
    category: NotificationCategorySchema,
    push: z.boolean(),
    inApp: z.boolean(),
    email: z.boolean(),
    alwaysOn: z.boolean(),
  })
  .strict();

export type NotificationPreferenceRow = z.infer<typeof NotificationPreferenceRowSchema>;

export const NotificationPreferencesViewSchema = z
  .object({ categories: z.array(NotificationPreferenceRowSchema) })
  .strict();

export type NotificationPreferencesView = z.infer<typeof NotificationPreferencesViewSchema>;
